/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

type FooterSlotPage = Promise<PageWithComponentData | null> | PageWithComponentData | null;

interface FooterColumnSlotsProps {
    /** Business-Manager-editable column (pageId "sfnext-footer-account"). */
    slotFooterAccount?: FooterSlotPage;
    /** Business-Manager-editable column (pageId "sfnext-footer-support"). */
    slotFooterSupport?: FooterSlotPage;
    /** Business-Manager-editable column (pageId "sfnext-footer-about"). */
    slotFooterAbout?: FooterSlotPage;
    /** Business-Manager-editable column (pageId "sfnext-footer-contact"). */
    slotFooterContact?: FooterSlotPage;
}

/**
 * Business-Manager-editable footer columns, additive alongside NavColumns' static grid
 * (not a replacement — see nav-columns.tsx for the account/customerSupport/about/contactUs
 * columns driven by FOOTER_NAV_COLUMNS). Mimics NavColumns' desktop 4-column grid layout;
 * each cell renders nothing until Business Manager authors content for that column's pageId.
 */
export default function FooterColumnSlots({
    slotFooterAccount,
    slotFooterSupport,
    slotFooterAbout,
    slotFooterContact,
}: FooterColumnSlotsProps): ReactElement | null {
    if (!slotFooterAccount && !slotFooterSupport && !slotFooterAbout && !slotFooterContact) {
        return null;
    }

    return (
        <div className="lg:grid grid-cols-4 gap-8">
            {slotFooterAccount && <Region page={slotFooterAccount} regionId="content" />}
            {slotFooterSupport && <Region page={slotFooterSupport} regionId="content" />}
            {slotFooterAbout && <Region page={slotFooterAbout} regionId="content" />}
            {slotFooterContact && <Region page={slotFooterContact} regionId="content" />}
        </div>
    );
}
